import { apiClient } from 'src/api/apiClient';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  supportedObjectsToFieldIds,
  getEntityIdMap,
  IntegrationsTools,
  DisconnectedStatus,
} from 'src/features/settings/integrations/constants/integrations';
import { SyncDirection } from 'src/features/settings/integrations/types';
import { isValidSyncCondition } from 'src/features/settings/integrations/utils/integrations';

import {
  AddObjectProperitesPayload,
  CompleteIntegrationPayload,
  GetInteagrationDataPlayload,
  GetInteagrationDataResult,
  GetSyncLogsPaylod,
  GetSyncLogsResultsData,
  GetTestDataPayload,
  IMapValuesRes,
  IntegrationStatusResult,
  IntegrationsPayload,
  UpdateGlobalSyncFlagPayload,
  UpdateIntegrationStatusPayload,
  UpdateMappingsPayload,
  ManualSyncContactsPayload,
} from './types';
import {
  ADD_CRM_OBJECT,
  BATCH_EXTRACT,
  CRM_DROPDOWN_VALUES,
  CUSTOM_FIELDS_LISTING_ALL_FIELDS,
  DISCONNECT_SCHEDULE,
  FETCH_ACTIVE_INTEGRATIONS,
  FETCH_ENUM_VALUES,
  GET_COMPANY_ENTITY_INFO,
  GET_CONTACT_ENTITY_INFO,
  GET_CRM_MAPPINGS,
  GET_CRM_OBJECT_PROPERTIES,
  GET_CRM_STAGES,
  GET_CUSTOMER_ID,
  GET_CUSTOM_FIELDS_DATA,
  GET_MANAGE_USER_DETAILS,
  GET_PUT_INTEGRATION_STATUS,
  GET_SYNC_DATA,
  GLOBAL_SYNC_FLAG,
  MAP_VALUES,
  MAP_VALUES_FETCH,
  POST_TEST_RUN,
  SAVE_CODE,
  UPDATE_CRM_MAPPING,
  SCHEDULE_SYNC,
  VALIDATE_CREDENTIALS,
  GET_ASK_AI_SIGNAL_DATA,
  GET_CUSTOMER_ICPS_LIST,
  SYNC_MANUAL_TRIGGER_CONTACTS,
  BATCH_EXTRACT_V2,
  BATCH_EXTRACT_V3,
} from 'src/constants/httpEndPoints';
import {
  FieldTypes,
  ITableData,
  RowType,
  SproutsFieldMocType,
} from 'src/features/settings/integrations/types';
import {
  getFieldMappingsMocData,
  IntegrationEntityNames,
  IntegrationStatus,
  PollingFrequencyOptions,
  selectedData,
} from 'src/features/settings/integrations/constants/integrations';
import {
  getMapValuesType,
  getPastDate,
  getSchedulePayload,
  getFilteredSupportedActivity,
  getFilteredFieldMappingsMocData,
  getSyncDirection,
} from 'src/features/settings/integrations/utils/integrations';
import {
  setReAuthIntegration,
  setCustomFields,
  setPicklistDropdownValues,
} from './integrations-v2.slice';
import { getTransformEntities } from './transformEntities';

interface AiSignalConfiguration {
  columnName?: string;
  displayName?: string;
  featureName?: 'AI_SIGNAL' | 'ASK_AI';
  type?: 'ACCOUNT' | 'CONTACT';
}

interface IntegrationDynamicField {
  fieldName: string;
  dbColumnName: string;
  fieldType: string;
}

const getAiSignalSourceColumn = (signal: AiSignalConfiguration): string => {
  if (signal.featureName === 'ASK_AI') {
    const askAiIndex = signal.columnName?.match(/^askAi(.+)$/i)?.[1];
    return askAiIndex ? `webSources${askAiIndex}` : '';
  }
  return signal.columnName ? `${signal.columnName}SourceUrl` : '';
};

const getAiSignalFields = (
  signals: AiSignalConfiguration[],
  entity: 'ACCOUNT' | 'CONTACT',
): IntegrationDynamicField[] =>
  signals.flatMap((signal) => {
    const signalEntity = signal.type?.toUpperCase() === 'CONTACT' ? 'CONTACT' : 'ACCOUNT';
    if (!signal.displayName || !signal.columnName || signalEntity !== entity) {
      return [];
    }
    const sourceColumn = getAiSignalSourceColumn(signal);
    const fields: IntegrationDynamicField[] = [
      {
        fieldName: signal.displayName,
        dbColumnName: signal.columnName,
        fieldType: 'String',
      },
    ];
    if (sourceColumn) {
      fields.push({
        fieldName: `${signal.displayName} SourceUrl`,
        dbColumnName: sourceColumn,
        fieldType: 'String',
      });
    }
    return fields;
  });

/**
 * Async thunk to fetch integration status.
 * @returns IntegrationStatusResult - Result containing integration status and tool name.
 */
export const getIntegrationStatus = createAsyncThunk<IntegrationStatusResult, IntegrationsPayload>(
  'integrations-v2/getIntegrationStatus',
  async (payload) => {
    const { customerId, emailId, providerName, integrationType } = payload;
    const res = await apiClient.post(GET_PUT_INTEGRATION_STATUS, {
      customerId,
      emailId,
      providerName,
    });
    const integrationKey = Object.keys(IntegrationsTools).find(
      (key) => IntegrationsTools[key].providerName === providerName,
    );
    const toolName = integrationKey || providerName.toLowerCase();

    return { integrationStatus: res.data, toolName, integrationType };
  },
);

/**
 * Async thunk to update integration status.
 * This thunk updates the integration status via an API call and optionally clears mappings if the status is DISCONNECTED or RESET.
 * @returns IntegrationStatusResult - Result containing updated integration status and tool name.
 */
export const updateIntegrationStatus = createAsyncThunk<
  IntegrationStatusResult,
  UpdateIntegrationStatusPayload
>('integrations-v2/updateIntegrationStatus', async (payload, { dispatch }) => {
  const { customerId, providerName, emailId, status: integrationStatus, integrationType } = payload;

  // Update integration status
  await apiClient.put(GET_PUT_INTEGRATION_STATUS, {
    customerId,
    providerName,
    emailId,
    status: integrationStatus,
  });

  // If status is DISCONNECTED or RESET, clear mappings
  if ([IntegrationStatus.DISCONNECTED, IntegrationStatus.RESET].includes(integrationStatus)) {
    await dispatch(clearMappings({ customerId, providerName, emailId }));
  }

  // Return updated integration status
  const integrationKey = Object.keys(IntegrationsTools).find(
    (key) => IntegrationsTools[key].providerName === providerName,
  );
  const toolName = integrationKey || payload.providerName.toLowerCase();
  return { integrationStatus, toolName, integrationType };
});

export const addObjectProperties = createAsyncThunk<string, AddObjectProperitesPayload>(
  'integrations-v2/addObjectProperties',
  async ({ numericCustomerId, emailId, providerName }) => {
    await apiClient.post(ADD_CRM_OBJECT, {
      customerId: numericCustomerId,
      emailID: emailId,
      providerName,
    });
    return 'Added Object properties';
  },
);

/**
 * Async thunk to fetch and set integration configuration data.
 * This thunk fetches integration data via various API calls and sets up configuration data for integrations.
 * @returns GetInteagrationDataResult - Result containing various integration configuration data.
 */
export const getSetConfigureIntegrationData = createAsyncThunk<
  GetInteagrationDataResult,
  GetInteagrationDataPlayload
>(
  'integrations-v2/setGetIntegrationData',
  async (
    {
      customerId,
      emailId,
      providerName,
      code,
      codeVerifier,
      addProperties,
      integrationType,
      clientName,
      globalSyncFlag,
      supportedObjects,
      crmTenantId,
      location,
      tokenBaseURL,
      authFields,
    },
    { dispatch, getState },
  ) => {
    // Fetch numeric customer ID
    const { data: numericCustomerId } = await apiClient.get(
      `${GET_CUSTOMER_ID}/?uniqueId=${customerId}`,
    );

    const integrationKey = Object.keys(IntegrationsTools).find(
      (key) => IntegrationsTools[key].providerName === providerName,
    );
    const integrationTool = integrationKey ? IntegrationsTools[integrationKey] : undefined;
    const authConfig = integrationTool?.authConfig;

    // Note: Validation is now handled in configure-integration page component
    // No validation here to avoid duplication and maintain consistency

    // Construct effective auth data combining legacy args and authFields
    const effectiveAuthData = { ...authFields };
    // if (apiKey) effectiveAuthData.apiKey = apiKey;
    // if (bundleAlias) effectiveAuthData.domain = bundleAlias; // Assuming domain maps to bundleAlias for legacy args

    // 2. Save Code / Complete Auth
    // Condition: We have an OAuth code OR we have valid API Key credentials
    const isOAuth = authConfig?.mode === 'OAUTH';
    const isApiKey = authConfig?.mode === 'API_KEY';
    const isHybrid = authConfig?.mode === 'HYBRID';
    const isDirect = authConfig?.mode === 'DIRECT';

    // Check if all required fields are present for API_KEY integrations
    const hasCredentials =
      (isApiKey || isHybrid || isDirect || isOAuth) &&
      authConfig?.fields.every((field) => {
        if (field.required) {
          return !!effectiveAuthData[field.key];
        }
        return true;
      });

    // Check if integration has POST_AUTH fields (like Google Analytics property_id)
    const hasPostAuthFields = authConfig?.fields?.some((f) => f.stage === 'POST_AUTH');

    // Save-code condition:
    // - OAuth with code: Always call save-code for initial OAuth
    // - OAuth with POST_AUTH credentials: Call save-code for property selection (Google Analytics)
    // - API_KEY/HYBRID/DIRECT: Call only when credentials are filled
    if (
      (isOAuth && code) ||
      (isOAuth && !code && hasCredentials && hasPostAuthFields) ||
      ((isApiKey || isHybrid || isDirect) && hasCredentials)
    ) {
      try {
        const saveCodePayload: any = {
          code,
          ...(codeVerifier && { codeVerifier }),
          customerId,
          emailID: emailId,
          providerName,
          authFields: effectiveAuthData, // Send the full authFields
          crmTenantId,
          location,
          tokenBaseURL,
        };

        // Map fields to top-level payload if payloadKey is defined (for legacy backend compatibility)
        // Skip file fields - they should stay inside authFields only
        authConfig?.fields.forEach((field) => {
          if (field.payloadKey && effectiveAuthData[field.key] && field.type !== 'file') {
            saveCodePayload[field.payloadKey] = effectiveAuthData[field.key];
          }
        });

        // Legacy fallback for specific args passed directly (if not in authFields)
        if (authFields?.apiKey) saveCodePayload.apiKey = authFields.apiKey;
        if (authFields?.bundleAlias) saveCodePayload.bundleAlias = authFields.bundleAlias;
        if (authFields?.tokenBaseURL) saveCodePayload.tokenBaseURL = authFields.tokenBaseURL;
        if (authFields?.location) saveCodePayload.location = authFields.location;
        if (authFields?.crmTenantId) saveCodePayload.crmTenantId = authFields.crmTenantId;

        await apiClient.post(SAVE_CODE, saveCodePayload);

        // For AUTH_ONLY integrations, auto-complete after successful OAuth
        const shouldAutoComplete = integrationType === 'AUTH' && !hasPostAuthFields;

        if (shouldAutoComplete) {
          await dispatch(
            updateIntegrationStatus({
              customerId,
              providerName,
              emailId,
              status: 'CONFIGURED',
              integrationType,
            }),
          ).unwrap();
        }
      } catch (error) {
        dispatch(setReAuthIntegration(true));
      }
    }

    // Add properties if addProperties flag is true, this will fetch the fields from CRM and save to sprouts data base.
    // If user add some new custome fields to crm we need to call this to fetch latest fields as well
    // Skip for AUTH_ONLY integrations as they don't have CRM/CMP properties
    if (addProperties && integrationType !== 'AUTH') {
      await dispatch(addObjectProperties({ emailId, numericCustomerId, providerName })).unwrap();
    }

    if (integrationType === 'AUTH') {
      return {
        userMappingsTableData: { commonData: {}, rowData: [] },
        fieldMappingsTableData: {}, // Record<string, ITableData>
        dealStageData: {}, // Record<string, string>
        prospectStagesTableData: { commonData: {}, rowData: [] },
        numericCustomerId,
        prospectStagesDropdownList: [],
        globalSyncFlag: {},
      };
    }

    // get integration data
    const objectPropertiesPayload = {
      customerId: numericCustomerId,
      emailId,
      providerName,
    };

    const getSprotesEmailsPayload = {
      uniqueId: customerId,
      pageNo: 0,
      pageSize: 1000,
      status: 'ACTIVE',
    };

    const crmMappingsPayload = {
      ...objectPropertiesPayload,
      isMandatory: false,
    };

    // Get global sync flag, try catch is added because we don't want to interrupt the flow if this throws any error
    // get global sync flag only for crm
    if (integrationType === 'CRM') {
      try {
        await dispatch(getGlobalSyncFlag({ customerId, emailId, providerName })).unwrap();
      } catch (error) {
        console.error('Failed to fetch global sync flag, using defaults:', error);
      }
    }

    // Fetch various integration data from API
    const [objectPropertiesRes, crmMappingsRes, prospectStagesRes, userRes] = await Promise.all([
      apiClient.post(GET_CRM_OBJECT_PROPERTIES, objectPropertiesPayload),
      apiClient.post(GET_CRM_MAPPINGS, crmMappingsPayload),
      apiClient.get(`${GET_CRM_STAGES}?customerId=${customerId}&operation=fetchProspectStages`),
      apiClient.post(GET_MANAGE_USER_DETAILS, getSprotesEmailsPayload),
    ]);

    const sproutsUserEmails: string[] =
      userRes.data?.list?.map((user: { email: string }) => user.email) || [];

    const objectProperties = objectPropertiesRes.data;
    const crmMappings = crmMappingsRes.data;

    let userMappingsTableData: ITableData = { commonData: {}, rowData: [] };

    try {
      const ownersIntegrationData = objectProperties.find(
        (obj: any) => obj.entityName === IntegrationEntityNames.Owners,
      );

      const crmOwnerMappings = crmMappings.find(
        (obj: any) => obj.entityName === IntegrationEntityNames.Owners,
      );

      if (ownersIntegrationData && ownersIntegrationData.propertiesList) {
        // check if the mappings are already present do not auto map the emails
        const enableAutomap = !crmOwnerMappings || crmOwnerMappings?.propertyMap === '{}';

        let propertyMap: { [key: string]: string } = {};
        try {
          propertyMap = crmOwnerMappings ? JSON.parse(crmOwnerMappings.propertyMap) : {};
        } catch (error) {
          console.error('Error parsing owner mappings', error);
        }

        userMappingsTableData = {
          rowData: sproutsUserEmails?.map((sproutsUserEmailId) => {
            const integrationUserEmailId =
              propertyMap[sproutsUserEmailId] ||
              (enableAutomap &&
              ownersIntegrationData?.propertiesList?.find(
                (property: string) => property === sproutsUserEmailId,
              )
                ? sproutsUserEmailId
                : '') ||
              '';
            return {
              sproutsUserEmailId,
              status: !!integrationUserEmailId,
              integrationUserEmailId,
            };
          }),
          commonData: {
            integrationUserEmailId: (
              ownersIntegrationData?.propertiesList.map((property: string) => property || '') || []
            )
              .sort()
              .map((property: string) => ({
                label: property,
                value: property,
              })),
          },
        };
      }
    } catch (error) {
      console.error('User mapping data not available for this integration:', error);
    }

    // get account and company fields types of sprouts
    let accountsEntityInfo: Record<string, string> = {};
    let contactsEntityInfo: Record<string, string> = {};
    let leadEntityInfo: Record<string, string> = {};
    try {
      const [accountsEntityInfoRes, contactsEntityInfoRes] = await Promise.all([
        apiClient.get(`${GET_COMPANY_ENTITY_INFO}/${customerId}`, {
          headers: {
            'X-Login-Email': emailId,
          },
        }),
        apiClient.get(`${GET_CONTACT_ENTITY_INFO}/${customerId}`, {
          headers: {
            'X-Login-Email': emailId,
          },
        }),
      ]);
      accountsEntityInfo = accountsEntityInfoRes.data;
      contactsEntityInfo = contactsEntityInfoRes.data;
      leadEntityInfo = contactsEntityInfoRes.data;
    } catch (error) {
      console.error('Failed to fetch account/contact entity info:', error);
    }

    const fieldsIntegraionData: Record<
      string,
      { propertiesList: string[]; keysList: string[]; typeList: string[]; keyActionList: string[] }
    > = {};
    const crmFieldMappings: Record<string, string> = {};
    objectProperties.forEach((obj: any) => {
      if (obj.entityName !== IntegrationEntityNames.Owners) {
        fieldsIntegraionData[obj.entityName] = {
          propertiesList: obj.propertiesList,
          keysList: obj.keysList,
          typeList: obj.typeList || [],
          keyActionList: obj.keyActionList || [],
        };
      }
    });

    crmMappings.forEach((obj: any) => {
      if (obj.entityName !== IntegrationEntityNames.Owners) {
        crmFieldMappings[obj.entityName] = obj.propertyMap;
      }
    });

    const getEnableAutoMapForEntity = (entityName: string) => {
      const entityMapping = crmMappings.find((obj: any) => obj.entityName === entityName);
      return !entityMapping || entityMapping.propertyMap === '{}';
    };

    const fieldMappingsTableData: Record<string, ITableData> = {};

    // show optional linkedin and email only for hubspot
    if (!['HUBSPOT', 'SALESFORCE', 'PIPEDRIVE'].includes(providerName)) {
      getFieldMappingsMocData(providerName).forEach((mapping) => {
        mapping.sproutsFields.forEach((field) => {
          if (field.optionalWithValue) {
            delete field.optionalWithValue;
            if (field.label === 'Linkedin Url' && field.required) {
              delete field.required;
            }
          }
        });
      });
    }

    // For HEYREACH, LinkedIn URL is mandatory for contacts as only contacts with LinkedIn URLs can be sent
    if (providerName === 'HEYREACH') {
      getFieldMappingsMocData(providerName).forEach((mapping) => {
        if (mapping.id === 'contacts') {
          mapping.sproutsFields.forEach((field) => {
            if (field.label === 'Linkedin Url') {
              field.required = true;
              // Remove optionalWithValue if it exists to enforce requirement
              if (field.optionalWithValue) {
                delete field.optionalWithValue;
              }
            }
          });
        }
      });
    }

    // delete listName and sub designation from contacts to all the customers except Sprouts.ai
    if (clientName !== 'Sprouts.ai') {
      getFieldMappingsMocData(providerName).forEach((mapping) => {
        if (mapping.id === 'contacts' || mapping.id === 'lead') {
          const newSproutsFields = mapping.sproutsFields.filter(
            (field) => field.label !== 'Sub-Seniority',
          );
          mapping.sproutsFields = newSproutsFields;
        }
      });
    }

    // Salesforce-specific field configurations
    if (providerName === 'SALESFORCE') {
      // Add Company History field for contacts and leads
      getFieldMappingsMocData(providerName).forEach((mapping) => {
        if (mapping.id === 'contacts' || mapping.id === 'lead') {
          // Check if companyHistory already exists to avoid duplicates
          const hasCompanyHistory = mapping.sproutsFields.some(
            (field) => field.id === 'companyHistory',
          );
          if (!hasCompanyHistory) {
            mapping.sproutsFields.push({
              id: 'companyHistory',
              label: 'Job History',
              onlyOutbound: true,
              selectedData: { ...selectedData },
            });
          }
        }
      });
    } else {
      // Remove ICP AI Summary field from company for non-Salesforce providers
      getFieldMappingsMocData(providerName).forEach((mapping) => {
        if (mapping.id === 'company') {
          const newSproutsFields = mapping.sproutsFields.filter(
            (field) => field.id !== 'icpContributingFactors',
          );
          mapping.sproutsFields = newSproutsFields;
        }
      });
    }

    // // removed account field in contacts and leads except for SALESFORCE and ZOHO
    // const includedProviders = ['SALESFORCE', 'ZOHO'];
    // if (!includedProviders.includes(providerName)) {
    //   FieldMappingsMocData.filter((field) => {
    //     if (!globalSyncFlag.isLeadEnabled && field.id === 'lead') {
    //       return false;
    //     }
    //     return true;
    //   }).forEach((mapping) => {
    //     if (mapping.id === 'contacts' || mapping.id === 'lead') {
    //       const newSproutsFields = mapping.sproutsFields.filter((field) => !field.isAccountField);
    //       mapping.sproutsFields = newSproutsFields;
    //     }
    //   });
    // }

    // Read custom fields already fetched by getCustomFields (which ran before this thunk)
    const reduxState = (getState as any)();
    const reduxCustomFieldsAccount: Array<{
      fieldName: string;
      dbColumnName: string;
      fieldType: string;
    }> = reduxState.integrationsV2?.customFieldsAccount || [];
    const reduxCustomFieldsContact: Array<{
      fieldName: string;
      dbColumnName: string;
      fieldType: string;
    }> = reduxState.integrationsV2?.customFieldsContact || [];
    const reduxAiSignalAccountFields: Array<{
      fieldName: string;
      dbColumnName: string;
      fieldType: string;
    }> = reduxState.integrationsV2?.aiSignalFieldsAccount || [];
    const reduxAiSignalContactFields: Array<{
      fieldName: string;
      dbColumnName: string;
      fieldType: string;
    }> = reduxState.integrationsV2?.aiSignalFieldsContact || [];

    const filteredMappings = getFilteredFieldMappingsMocData(
      globalSyncFlag,
      supportedObjects,
      providerName,
    ).map((mapping) => ({
      ...mapping,
      sproutsFields: mapping.sproutsFields.filter(
        (field) => field.featureFlag !== 'customAiSignals',
      ),
    }));

    // Inject custom fields into sproutsFields for each entity before building table rows
    filteredMappings.forEach((mapping) => {
      const isContact =
        mapping.entity === IntegrationEntityNames.Contact ||
        mapping.entity === IntegrationEntityNames.Lead;
      const isAccount = mapping.entity === IntegrationEntityNames.Companies;

      const customFields = isContact
        ? reduxCustomFieldsContact
        : isAccount
        ? reduxCustomFieldsAccount
        : [];

      if (isAccount || isContact) {
        const dynamicAiFields = isAccount ? reduxAiSignalAccountFields : reduxAiSignalContactFields;
        const existingIds = new Set(mapping.sproutsFields.map((field) => field.id));
        const aiSignalFields = dynamicAiFields
          .filter((field) => field.dbColumnName && !existingIds.has(field.dbColumnName))
          .map((field) => ({
            label: field.fieldName,
            id: field.dbColumnName,
            value: field.dbColumnName,
            selectedData: { ...selectedData },
            isAiField: true,
            onlyOutbound: true,
          }));

        mapping.sproutsFields.push(...aiSignalFields);
      }

      if (customFields.length > 0) {
        const existingIds = new Set(mapping.sproutsFields.map((f: any) => f.id));
        const newFields = customFields
          .filter((f) => f.dbColumnName && !existingIds.has(f.dbColumnName))
          .map((f) => ({
            label: f.fieldName || '',
            id: f.dbColumnName || '',
            value: f.dbColumnName || '',
            selectedData: { ...selectedData },
            isCustomField: true,
          }));
        mapping.sproutsFields.push(...newFields);
      }
    });

    filteredMappings.forEach((field) => {
      let exsitingMappings: Record<string, any> = {};
      try {
        exsitingMappings = crmFieldMappings ? JSON.parse(crmFieldMappings[field.entity]) : {};
      } catch (error) {
        console.error('Failed to parse existing field mappings:', error);
      }

      const enableFieldMappingsAutoMapForEntity = getEnableAutoMapForEntity(field.entity);

      const commonDataHashTypes: Record<string, string> = {};
      const commonDataHashActions: Record<string, string> = {};
      const commonData = {
        integrationField:
          fieldsIntegraionData[field.entity]?.propertiesList?.map((property, idx) => {
            const value = fieldsIntegraionData[field.entity].keysList[idx];
            const type = fieldsIntegraionData[field.entity].typeList?.[idx];
            const action = fieldsIntegraionData[field.entity].keyActionList?.[idx] || 'All';
            commonDataHashTypes[value] = type;
            commonDataHashActions[value] = action;

            return {
              label: property,
              value,
              type,
              action,
            };
          }) || [],
      };

      const rowData: RowType[] = field.sproutsFields.map((sproutsField) => {
        const {
          label,
          id,
          typeId,
          defaultMapValues,
          supportedTypes,
          testValue,
          selectedData: {
            dontSyncToSprouts,
            from_sprouts,
            nosync,
            toSprouts,
            to_crm_if_blank,
            value,
          },
        } = sproutsField;
        const fieldData = exsitingMappings[`${field.id}.${id}`];
        let currentType;

        let integrationField =
          fieldData?.value ||
          (enableFieldMappingsAutoMapForEntity &&
            commonData.integrationField.find(
              (data) =>
                data.value?.toLowerCase() === id?.toLowerCase() ||
                data.label?.toLowerCase() === label?.toLowerCase(),
            )?.value) ||
          value;

        if (integrationField) {
          const foundData = commonData.integrationField.find(
            (data) => data.value?.toLowerCase() === integrationField?.toLowerCase(),
          );
          currentType = foundData?.type;
        }
        const integrationKeyForDropdown = Object.keys(IntegrationsTools).find(
          (key) => IntegrationsTools[key].providerName === providerName,
        );
        const { dropdownFieldTypes, fieldTypes } = integrationKeyForDropdown
          ? IntegrationsTools[integrationKeyForDropdown]
          : { dropdownFieldTypes: [], fieldTypes: {} };
        let sproutsFieldType: string | undefined;
        if (field.entity === IntegrationEntityNames.Companies) {
          sproutsFieldType = accountsEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Contact) {
          sproutsFieldType = contactsEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Lead) {
          sproutsFieldType = leadEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Call) {
          sproutsFieldType = contactsEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Task) {
          sproutsFieldType = contactsEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Meeting) {
          sproutsFieldType = contactsEntityInfo[testValue || typeId || id];
        } else if (field.entity === IntegrationEntityNames.Sending) {
          sproutsFieldType = contactsEntityInfo[testValue || typeId || id];
        }

        sproutsFieldType = sproutsFieldType
          ?.replace('java.lang.', '')
          .replace('java.util.', '')
          .split('<')[0];
        const integrationFieldType =
          (integrationField && commonDataHashTypes[integrationField]) || undefined;

        let isTypeMistachError = false;

        if (supportedTypes?.length && integrationFieldType) {
          isTypeMistachError = !supportedTypes.some((type) =>
            fieldTypes[type as FieldTypes].includes(integrationFieldType),
          );
        } else if (
          sproutsFieldType &&
          fieldTypes[sproutsFieldType as FieldTypes] &&
          integrationFieldType
        ) {
          isTypeMistachError =
            !fieldTypes[sproutsFieldType as FieldTypes].includes(integrationFieldType);
        }

        if (
          clientName === 'Sprouts.ai' &&
          (supportedTypes?.includes('String') || sproutsFieldType === 'String') &&
          integrationFieldType === 'reference'
        ) {
          isTypeMistachError = false;
        }

        if (clientName === 'Sprouts.ai' && integrationFieldType === 'textarea') {
          isTypeMistachError = false;
        }

        // Get property action for the selected integration field
        const propertyAction = integrationField
          ? (commonDataHashActions[integrationField] as 'All' | 'Create' | 'Update' | 'None') ||
            'All'
          : undefined;

        // For onlyOutbound fields, force inbound sync to be disabled
        const isOnlyOutbound = sproutsField.onlyOutbound === true;

        // Check if field is non-editable (property action is 'None')
        const isNonEditable = propertyAction === 'None';

        return {
          ...sproutsField,
          sproutsField: label,
          integrationField,
          status: !!integrationField,
          supportedTypes,
          mapValues: defaultMapValues || getMapValuesType(dropdownFieldTypes, currentType),
          propertyAction,
          sproutsToCrm: {
            to_crm_if_blank: fieldData?.to_crm_if_blank ?? to_crm_if_blank,
            // Disable outbound sync for non-editable fields
            from_sprouts: isNonEditable ? false : fieldData?.from_sprouts ?? from_sprouts,
            // Force nosync for non-editable fields
            nosync: isNonEditable ? true : fieldData?.nosync ?? nosync,
          },
          crmToSprouts: {
            // Enforce onlyOutbound: inbound sync must be disabled.
            toSprouts: isOnlyOutbound
              ? false
              : fieldData?.toSprouts === false && toSprouts === true && globalSyncFlag?.intoSprouts
              ? true
              : fieldData?.toSprouts ?? toSprouts,
            dontSyncToSprouts: isOnlyOutbound
              ? true
              : fieldData?.dontSyncToSprouts ?? dontSyncToSprouts,
          },
          sproutsFieldType,
          integrationFieldType,
          typeMistachError: isTypeMistachError && (sproutsFieldType || supportedTypes?.length),
        };
      });

      fieldMappingsTableData[field.entity] = { rowData, commonData };
    });

    const dealStages: string[] =
      objectProperties.find((obj: any) => obj.entityName === IntegrationEntityNames.DealStages)
        ?.propertiesList || [];

    const dealStageData: Record<string, string> = {};
    const dealStageSavedMappings = crmMappings.find(
      (obj: any) => obj.entityName === IntegrationEntityNames.DealStages,
    );
    if (dealStageSavedMappings) {
      const dealStageProperties = JSON.parse(dealStageSavedMappings.propertyMap || '{}');
      dealStages.forEach((stage) => {
        dealStageData[stage] = dealStageProperties[stage]?.value || '';
      });
    }

    const prospectStagesDropdownList: string[] =
      objectProperties.find((obj: any) => obj.entityName === IntegrationEntityNames.ProspectStages)
        ?.propertiesList || [];
    // const prospectStagesRes = await apiClient.get(
    //   `${GET_CRM_STAGES}?customerId=${customerId}&operation=fetchProspectStages`,
    // );

    const prospectsSavedMappings = crmMappings.find(
      (obj: any) => obj.entityName === IntegrationEntityNames.ProspectStages,
    );

    const prospectsSavedProperties = JSON.parse(prospectsSavedMappings?.propertyMap || '{}');

    const enableProspectStagesAutoMap = getEnableAutoMapForEntity(
      IntegrationEntityNames.ProspectStages,
    );

    const prospectStagesRowData = (prospectStagesRes.data || []).map((val: string) => {
      const integrationValue =
        prospectsSavedProperties[val]?.value ||
        (enableProspectStagesAutoMap
          ? prospectStagesDropdownList.find((listVal) => listVal === val) || ''
          : '');
      return {
        id: val,
        sproutsValue: val,
        integrationValue,
        status: !!integrationValue,
      };
    });

    const prospectStagesCommonData = prospectStagesDropdownList.map((val) => {
      return {
        label: val,
        value: val,
      };
    });

    const prospectStagesTableData: ITableData = {
      rowData: prospectStagesRowData,
      commonData: { integrationValue: prospectStagesCommonData },
    };

    // Extract only filterable fields from objectProperties for optimized Redux storage
    const filterableFieldsByEntity: Record<
      string,
      Record<string, { name: string; type: string }>
    > = {};
    if (objectProperties && Array.isArray(objectProperties)) {
      objectProperties.forEach((entity: any) => {
        const { entityName, filterableFieldsMap } = entity;
        if (entityName && filterableFieldsMap) {
          filterableFieldsByEntity[entityName] = filterableFieldsMap;
        }
      });
    }

    return {
      userMappingsTableData,
      fieldMappingsTableData,
      numericCustomerId: numericCustomerId as number,
      dealStages,
      dealStageData,
      prospectStagesDropdownList,
      prospectStagesTableData,
      globalSyncFlag,
      filterableFieldsByEntity,
    };
  },
);

/**
 * Async thunk to update user mappings.
 * This thunk updates user mappings via `<<ADMIN_CONFIG_URL>>/crm-sprouts-properties/map`.
 */
export const updateUserMappings = createAsyncThunk<void, UpdateMappingsPayload>(
  'integrations-v2/udpdateUserMappings',
  async (payload) => {
    await apiClient.post(UPDATE_CRM_MAPPING, payload);
  },
);

/**
 * Async thunk to fetch custom fields for a given customer.
 * This thunk fetches custom fields via `${REACT_APP_BASE_API_FILTER_URL}/getCustomFields` and updates the local state with the retrieved data.
 */
export const getCustomFields = createAsyncThunk<
  void,
  { customerId: string; emailId: string; providerName?: string; globalSyncFlag?: any },
  { dispatch: any }
>(
  'integrations-v2/getCustomFields',
  async ({ customerId, emailId, providerName, globalSyncFlag }, { dispatch }) => {
    const results = await Promise.allSettled([
      apiClient.post(CUSTOM_FIELDS_LISTING_ALL_FIELDS, {
        uniqueId: customerId,
        emailId,
        entity: 'account',
      }),
      apiClient.post(CUSTOM_FIELDS_LISTING_ALL_FIELDS, {
        uniqueId: customerId,
        emailId,
        entity: 'contact',
      }),
      apiClient.post(
        GET_ASK_AI_SIGNAL_DATA,
        {},
        {
          headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
        },
      ),
      apiClient.post(GET_CUSTOMER_ICPS_LIST, {
        uniqueId: customerId,
        emailId: emailId,
      }),
    ]);

    // Extract results
    const accountFieldsRes = results[0].status === 'fulfilled' ? results[0].value : { data: [] };
    const contactFieldsRes = results[1].status === 'fulfilled' ? results[1].value : { data: [] };
    const customAiFieldsRes = results[2].status === 'fulfilled' ? results[2].value : { data: [] };
    const icpFieldsRes =
      results[3].status === 'fulfilled'
        ? results[3].value
        : { data: { accounts: [], contacts: [] } };

    // Log errors for failed requests
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`API call ${index} failed:`, result.reason);
      }
    });

    const customFieldsAccountOptions: Record<string, (string | number)[]> = {};
    const customFieldsContactOptions: Record<string, (string | number)[]> = {};
    const customFieldsLeadOptions: Record<string, (string | number)[]> = {};

    // Store raw custom fields data in Redux for outbound conditional sync
    const accountCustomFieldsData =
      accountFieldsRes?.data && Array.isArray(accountFieldsRes.data)
        ? accountFieldsRes.data.map((field: any) => ({
            fieldName: field.fieldName || '',
            dbColumnName: field.dbColumnName || '',
            fieldType: field.fieldType || '',
          }))
        : [];

    const contactCustomFieldsData =
      contactFieldsRes?.data && Array.isArray(contactFieldsRes.data)
        ? contactFieldsRes.data.map((field: any) => ({
            fieldName: field.fieldName || '',
            dbColumnName: field.dbColumnName || '',
            fieldType: field.fieldType || '',
          }))
        : [];

    // Store AI signal fields data (these are dynamic custom AI signals configured by user)
    const aiSignalAccountFieldsData =
      customAiFieldsRes?.data && Array.isArray(customAiFieldsRes.data)
        ? getAiSignalFields(customAiFieldsRes.data, 'ACCOUNT')
        : [];
    const aiSignalContactFieldsData =
      customAiFieldsRes?.data && Array.isArray(customAiFieldsRes.data)
        ? getAiSignalFields(customAiFieldsRes.data, 'CONTACT')
        : [];

    // Store ICP fields for accounts (these are dynamic ICP configurations)
    const icpAccountFieldsData =
      icpFieldsRes?.data?.accounts && Array.isArray(icpFieldsRes.data.accounts)
        ? icpFieldsRes.data.accounts
            .filter((account: any) => account.active === true)
            .map((account: any) => ({
              fieldName: account.nameOfFilter || '',
              dbColumnName: account.nameOfFilter || '', // ICP fields use same name for both
              fieldType: 'String', // Account ICP scores are typically strings
            }))
        : [];

    // Store ICP fields for contacts
    const icpContactFieldsData =
      icpFieldsRes?.data?.contacts && Array.isArray(icpFieldsRes.data.contacts)
        ? icpFieldsRes.data.contacts
            .filter((contact: any) => contact.active === true)
            .map((contact: any) => ({
              fieldName: contact.nameOfFilter || '',
              dbColumnName: contact.nameOfFilter || '',
              fieldType: 'String',
            }))
        : [];

    // Save accounts dropdown fields options
    if (accountFieldsRes?.data && Array.isArray(accountFieldsRes.data)) {
      accountFieldsRes.data.forEach((field: any) => {
        if (field.fieldSelectOptions && field.fieldName) {
          customFieldsAccountOptions[field.fieldName] = field.fieldSelectOptions.map(
            (val: any) => val.title,
          );
        }
      });
    }

    // Save contact dropdown fields options
    if (contactFieldsRes?.data && Array.isArray(contactFieldsRes.data)) {
      contactFieldsRes.data.forEach((field: any) => {
        if (field.fieldSelectOptions && field.fieldName) {
          customFieldsContactOptions[field.fieldName] = field.fieldSelectOptions.map(
            (val: any) => val.title,
          );
        }
      });
    }

    // Dispatch action to store all dynamic fields in Redux
    dispatch(
      setCustomFields({
        accountFields: accountCustomFieldsData,
        contactFields: contactCustomFieldsData,
        aiSignalAccountFields: aiSignalAccountFieldsData,
        aiSignalContactFields: aiSignalContactFieldsData,
        icpAccountFields: icpAccountFieldsData,
        icpContactFields: icpContactFieldsData,
      }),
    );

    // Pre-populate picklistDropdownValues with custom field dropdown options
    // This makes them immediately available for outbound conditional sync without API calls
    const accountEntityKey = `${providerName}_ACCOUNT`;
    const contactEntityKey = `${providerName}_CONTACTS`;

    // Populate account custom field dropdowns
    accountCustomFieldsData.forEach((field) => {
      const options = customFieldsAccountOptions[field.fieldName];
      if (options && Array.isArray(options) && options.length > 0) {
        dispatch(
          setPicklistDropdownValues({
            entityKey: accountEntityKey,
            fieldName: field.dbColumnName, // Use dbColumnName as the key
            options: options.map((val) => ({
              label: String(val),
              value: String(val),
            })),
          }),
        );
      }
    });

    // Populate contact custom field dropdowns
    contactCustomFieldsData.forEach((field) => {
      const options = customFieldsContactOptions[field.fieldName];
      if (options && Array.isArray(options) && options.length > 0) {
        dispatch(
          setPicklistDropdownValues({
            entityKey: contactEntityKey,
            fieldName: field.dbColumnName, // Use dbColumnName as the key
            options: options.map((val) => ({
              label: String(val),
              value: String(val),
            })),
          }),
        );
      }
    });

    // Save lead dropdown fields options (same as contact)
    if (contactFieldsRes?.data && Array.isArray(contactFieldsRes.data)) {
      contactFieldsRes.data.forEach((field: any) => {
        if (field.fieldSelectOptions && field.fieldName) {
          customFieldsLeadOptions[field.fieldName] = field.fieldSelectOptions.map(
            (val: any) => val.title,
          );
        }
      });
    }

    // Process ICP fields for accounts
    const icpAccountFields: SproutsFieldMocType[] = [];
    if (icpFieldsRes?.data?.accounts?.length) {
      icpFieldsRes.data.accounts.forEach((account: any) => {
        if (account.active === true) {
          const filterName = account.nameOfFilter || ``;
          const filterFields = [
            {
              label: filterName,
              id: filterName,
              value: filterName,
              selectedData: { ...selectedData },
              customeFieldValues: filterName
                ? { [filterName]: customFieldsAccountOptions[filterName] || [] }
                : undefined,
              isIcpField: true,
              onlyOutbound: true,
            },
          ];
          icpAccountFields.push(...filterFields);
        }
      });
    }

    // Process ICP fields for contacts
    const icpContactFields: SproutsFieldMocType[] = [];
    if (icpFieldsRes?.data?.contacts?.length) {
      icpFieldsRes.data.contacts.forEach((contact: any) => {
        if (contact.active === true) {
          const filterName = contact.nameOfFilter || ``;
          const filterFields = [
            {
              label: filterName,
              id: filterName,
              value: filterName,
              selectedData: { ...selectedData },
              customeFieldValues: filterName
                ? { [filterName]: customFieldsContactOptions[filterName] || [] }
                : undefined,
              isIcpField: true,
              onlyOutbound: true,
            },
          ];
          icpContactFields.push(...filterFields);
        }
      });
    }

    // If custom fields are retrieved successfully for contacts
    if (contactFieldsRes.data?.length > 0) {
      // Find the existing field mapping for contacts
      const contactsFieldMapping = getFieldMappingsMocData(providerName).find(
        (item) => item.entity === 'Contact',
      );
      const leadsFieldMapping = getFieldMappingsMocData(providerName).find(
        (item) => item.entity === 'Lead',
      );

      if (contactsFieldMapping) {
        // Process retrieved custom fields
        const newContactFields: SproutsFieldMocType[] = contactFieldsRes.data?.length
          ? contactFieldsRes.data.map((fieldData: any) => ({
              label: fieldData?.fieldName || '',
              id: fieldData?.dbColumnName || '',
              value: fieldData?.dbColumnName || '',
              selectedData: selectedData,
              customeFieldValues:
                customFieldsContactOptions[fieldData?.fieldName || ''] || undefined,
              isAiField: fieldData?.isAiField || false,
              isCustomField: true,
            }))
          : [];

        // Remove duplicates based on id instead of label
        const idMap: Record<string, boolean> = {};
        const uniqueContactData: SproutsFieldMocType[] = [];

        [...contactsFieldMapping.sproutsFields, ...newContactFields].forEach((obj) => {
          if (!idMap[obj.id]) {
            idMap[obj.id] = true;
            uniqueContactData.push(obj);
          } else {
            console.warn(
              `Duplicate ID found for contact field: ${obj.id} with label: ${obj.label}`,
            );
          }
        });

        // Update contacts field mapping with unique merged fields
        contactsFieldMapping.sproutsFields = uniqueContactData;
      }

      if (
        leadsFieldMapping &&
        ['SALESFORCE', 'ZOHO', 'MICROSOFT', 'PARDOT'].includes(providerName || '')
      ) {
        // Process retrieved custom fields for leads
        const newLeadFields: SproutsFieldMocType[] = contactFieldsRes.data?.length
          ? contactFieldsRes.data.map((fieldData: any) => ({
              label: fieldData?.fieldName || '',
              id: fieldData?.dbColumnName || '',
              value: fieldData?.dbColumnName || '',
              selectedData: selectedData,
              customeFieldValues: customFieldsLeadOptions[fieldData?.fieldName || ''] || undefined,
              isAiField: fieldData?.isAiField || false,
              isCustomField: true,
            }))
          : [];

        // Remove duplicates based on id instead of label
        const idMap: Record<string, boolean> = {};
        const uniqueLeadData: SproutsFieldMocType[] = [];

        [...leadsFieldMapping.sproutsFields, ...newLeadFields].forEach((obj) => {
          if (!idMap[obj.id]) {
            idMap[obj.id] = true;
            uniqueLeadData.push(obj);
          } else {
            console.warn(`Duplicate ID found for lead field: ${obj.id} with label: ${obj.label}`);
          }
        });

        // Update leads field mapping with unique merged fields
        leadsFieldMapping.sproutsFields = uniqueLeadData;
      }
    }

    // If custom fields, AI signals, or ICP fields are retrieved successfully for accounts
    if (
      accountFieldsRes.data?.length > 0 ||
      aiSignalAccountFieldsData.length > 0 ||
      icpAccountFields.length > 0
    ) {
      // Find the existing field mapping for accounts
      const accountsFieldMapping = getFieldMappingsMocData(providerName).find(
        (item) => item.entity === 'Companies',
      );
      if (accountsFieldMapping) {
        // Process custom account fields
        const newAccountFields: SproutsFieldMocType[] = accountFieldsRes.data?.length
          ? accountFieldsRes.data.map((fieldData: any) => ({
              label: fieldData?.fieldName || '',
              id: fieldData?.dbColumnName || '',
              value: fieldData?.dbColumnName || '',
              selectedData: selectedData,
              customeFieldValues:
                customFieldsAccountOptions[fieldData?.fieldName || ''] || undefined,
              onlyOutbound: fieldData?.fieldName?.match(/^integratedData\d+$/) ? true : false,
              isAiField: fieldData?.isAiField || false,
              isCustomField: true,
            }))
          : [];

        // Process AI signals fields
        const newAiSignalFields: SproutsFieldMocType[] = aiSignalAccountFieldsData.map((field) => ({
          label: field.fieldName,
          value: field.dbColumnName,
          id: field.dbColumnName,
          selectedData: { ...selectedData },
          isAiField: true,
          featureFlag: 'customAiSignals',
          onlyOutbound: true,
        }));

        // Remove duplicates based on id
        const idMap: Record<string, boolean> = {};
        const uniqueAccountData: SproutsFieldMocType[] = [];

        [
          ...accountsFieldMapping.sproutsFields,
          ...newAccountFields,
          ...newAiSignalFields,
          ...icpAccountFields,
        ].forEach((obj) => {
          if (!idMap[obj.id]) {
            idMap[obj.id] = true;
            uniqueAccountData.push(obj);
          } else {
            console.warn(
              `Duplicate ID found for account field: ${obj.id} with label: ${obj.label}`,
            );
          }
        });

        // Update accounts field mapping with unique merged fields
        accountsFieldMapping.sproutsFields = uniqueAccountData;
      }
    }
  },
);

/**
 * Async thunk to complete integration setup.
 * This thunk handles the completion of integration setup including historic sync and updating integration status.
 * if inbound is active we will call
 */
export const completeIntegration = createAsyncThunk<void, CompleteIntegrationPayload>(
  'integrations-v2/completeIntegration',
  async (
    {
      customerId,
      providerName,
      emailId,
      intoSprouts,
      historicInterval,
      isHistoricFetchEnabled,
      syncFrequency,
      integrationType,
      isLeadEnabled,
      dealEnabled,
      callEnabled,
      meetingEnabled,
      taskEnabled,
      emailEnabled,
      emailAsTaskEnabled,
    },
    { dispatch },
  ) => {
    // Generate unique group ID for the sync job
    const groupId = new Date().getTime();

    // Get integration configuration
    const integrationTool = Object.values(IntegrationsTools).find(
      (tool) => tool.providerName === providerName,
    );
    const supportedActivity = integrationTool?.supportedActivity || [];
    const filteredSupportedActivity = getFilteredSupportedActivity(supportedActivity, {
      callEnabled,
      meetingEnabled,
      taskEnabled,
      emailEnabled,
      emailAsTaskEnabled,
    });
    const entityIdMap = getEntityIdMap(providerName);

    // Perform integration setup if sync into Sprouts is enabled
    if (intoSprouts && integrationType !== 'CMP') {
      // Prepare base payload for historic sync job
      const basePayload: any = {
        provider: providerName,
        job_type: isHistoricFetchEnabled ? 'HISTORIC' : 'ORCHESTRATE',
        provider_entity: `${providerName}_ACCOUNT`,
        source: 'API',
        group_id: groupId,
        historic_interval: historicInterval,
      };

      // Adjust historic sync start and end timestamps
      const now = new Date();
      const previousDay = new Date(now);
      previousDay.setHours(0, 0, 0, 0);
      previousDay.setDate(now.getDate() - 1);

      // Set default historic sync interval to one day if not provided
      const timestamp = getPastDate(historicInterval);
      basePayload.query_start_timestamp = isHistoricFetchEnabled
        ? timestamp.query_start_timestamp || previousDay.toISOString()
        : previousDay.toISOString();
      basePayload.query_end_timestamp = isHistoricFetchEnabled
        ? timestamp.query_end_timestamp || now.toISOString()
        : now.toISOString();

      // Determine split unit based on historic sync interval
      let split_unit = '';
      if (!isHistoricFetchEnabled) {
        split_unit = 'FIFTEEN_SECONDS'; // for interval of a day
      } else if (historicInterval?.includes('Month')) {
        split_unit = 'WEEK';
      } else if (historicInterval?.includes('Week')) {
        split_unit = 'DAY';
      }
      basePayload.split_unit = split_unit;

      // Perform historic sync for all supported activity entities
      const syncPromises = filteredSupportedActivity
        .map((activityName) => {
          const fieldId = supportedObjectsToFieldIds[activityName];
          const providerEntity = entityIdMap[fieldId];

          // Filter out LEAD/DEAL entities when their toggles are disabled
          if (
            providerEntity &&
            (providerEntity !== `${providerName}_LEAD` || isLeadEnabled) &&
            (providerEntity !== `${providerName}_DEALS` || dealEnabled)
          ) {
            return apiClient.post(
              GET_SYNC_DATA,
              { ...basePayload, provider_entity: providerEntity },
              { headers: { customer_id: customerId } },
            );
          }
          return null;
        })
        .filter(Boolean);

      await Promise.all(syncPromises);

      // Update sync schedules for the integration
      await apiClient.post(
        SCHEDULE_SYNC,
        getSchedulePayload(providerName, syncFrequency, filteredSupportedActivity, isLeadEnabled),
        {
          headers: { customer_id: customerId },
        },
      );
    }

    // CMP integrations setup
    // Skip schedule API for outbound-only integrations (no inbound sync needed)
    const syncDirection = getSyncDirection(providerName);
    if (integrationType === 'CMP' && syncDirection !== SyncDirection.OUTBOUND_ONLY) {
      await apiClient.post(
        SCHEDULE_SYNC,
        getSchedulePayload(providerName, syncFrequency, filteredSupportedActivity, isLeadEnabled),
        {
          headers: { customer_id: customerId },
        },
      );
    }

    // Update integration status to CONFIGURED
    await dispatch(
      updateIntegrationStatus({
        customerId,
        integrationType,
        emailId,
        providerName,
        status: IntegrationStatus.CONFIGURED,
      }),
    ).unwrap();
  },
);

/**
 * Async thunk to clear mappings.
 * This thunk clears mappings for various entities associated with the integration.
 */
export const clearMappings = createAsyncThunk<void, IntegrationsPayload>(
  'inteagration-v2/clearMappings',
  async ({ providerName, customerId, emailId }) => {
    const res = await apiClient.get(`${GET_CUSTOMER_ID}?uniqueId=${customerId}`);
    const customerNumericId = res.data;
    const payload = {
      providerName: providerName,
      emailId: emailId,
      customerId: customerNumericId,
      mappedEntities: [
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Owners,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Companies,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.ProspectStages,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.DealStages,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Contact,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Lead,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Deals,
        },
        // CMP entities
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Call,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Task,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Meeting,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.Sending,
        },
        {
          mappingJson: {},
          noMappedFields: 0,
          entity: IntegrationEntityNames.SequenceSteps,
        },
      ],
    };
    await apiClient.post(UPDATE_CRM_MAPPING, payload);
  },
);

/**
 * Async thunk to fetch sync data.
 * This thunk fetches synchronization logs data from the server.
 */
export const getSyncData = createAsyncThunk<GetSyncLogsResultsData, GetSyncLogsPaylod>(
  'integration-v2/getSyncData',
  async (
    { customerId, providerName, size, page, source, status, startDate, endDate, entity },
    { dispatch, getState },
  ) => {
    // Fetch sync data from the server
    const { data } = await apiClient.get(
      `${GET_SYNC_DATA}?provider=${providerName}${
        !entity || entity === 'All' ? '' : `&provider_entity=${entity}`
      }${
        source
          ? source
          : '&job_types=OUTBOUND&job_types=HISTORIC&job_types=ORCHESTRATE&job_types=EXTRACT&job_types=TRANSFORM&job_types=DAG_ORCHESTRATE'
      }${!status || status === 'All' ? '' : `&job_status=${status}`}${
        startDate && startDate ? `&from_timestamp=${startDate}&to_timestamp=${endDate}` : ''
      }&no_parent=true&page=${page || 0}&size=${size || 1}`,
      {
        headers: { customer_id: customerId },
      },
    );

    // Group sync data by group ID
    const groupContent: Record<number, any[]> = {};
    data.content.forEach((content: any) => {
      if (content.group_id in groupContent) {
        const temp = groupContent[content?.group_id];
        temp.push(content);
        groupContent[content?.group_id] = temp;
      } else {
        const contents = [content];
        groupContent[content?.group_id] = contents;
      }
    });

    // Find the latest group and fetch data if not found
    const numericValues = Object.keys(groupContent)
      .filter((value) => value !== 'null')
      .map(Number);
    const maxValue = Math.max(...numericValues);
    const latestGroup = groupContent[maxValue];
    if (!latestGroup) {
      const state: any = getState();
      const integrationKeyForSync = Object.keys(IntegrationsTools).find(
        (key) => IntegrationsTools[key].providerName === providerName,
      );
      const integrationTool =
        state?.integrationsV2?.integrationTools?.[
          integrationKeyForSync || providerName.toLowerCase()
        ];
      // if (integrationTool?.integrationStatus === IntegrationStatus.CONFIGURED) {
      //   // Retry fetching data after a delay if the latest group is not found
      //   setTimeout(() => {
      //     dispatch(getSyncData({ customerId, providerName, size }));
      //   }, 1000 * 30); // Retry after 30 seconds
      // }
    }
    const integrationKeyForToolId = Object.keys(IntegrationsTools).find(
      (key) => IntegrationsTools[key].providerName === providerName,
    );
    return {
      data: data.content,
      toolId: integrationKeyForToolId || providerName.toLowerCase(),
      totalSyncData: data.totalElements,
      totalPages: data.totalPages,
    };
  },
);

/**
 * Async thunk to fetch test run data.
 * This thunk fetches test run data for multiple provider entities.
 */
export const getTestRunData = createAsyncThunk<Record<string, any[]>, GetTestDataPayload>(
  'integration-v2/getTestRunData',
  async ({
    providerName,
    customerId,
    query_end_timestamp,
    query_start_timestamp,
    type,
    isLeadEnabled,
    dealEnabled,
    supportedObjects = [],
  }) => {
    let data: Record<string, any[]> = {};
    const headers = { customer_id: customerId };

    // Fetch provider entity S3 URLs based on supported objects
    const getProviderEntities = () => {
      // Special case: Mailchimp
      if (providerName === 'MAILCHIMP') {
        return [`${providerName}_LIST`];
      }
      // Special case: Adobe Marketo - syncs through LIST entity only
      if (providerName === 'ADOBE_MARKETO') {
        return [`${providerName}_LIST`];
      }
      if (providerName === 'SMARTLEAD') {
        return [`${providerName}_SEQUENCE`];
      }
      // Get entity ID mapping for the provider
      const entityIdMap = getEntityIdMap(providerName);

      // Convert supported objects to field IDs and map to provider entity IDs
      let supportedMappingIds =
        supportedObjects?.map((obj) => supportedObjectsToFieldIds[obj]).filter(Boolean) || [];

      // Filter out lead/deal entities when their toggles are false
      if (!isLeadEnabled) {
        supportedMappingIds = supportedMappingIds.filter((id) => id !== 'lead');
      }
      if (!dealEnabled) {
        supportedMappingIds = supportedMappingIds.filter((id) => id !== 'opportunity');
      }

      if (supportedMappingIds.length > 0) {
        let entities = supportedMappingIds
          .map((fieldId) => entityIdMap[fieldId])
          .filter((entity) => entity && (entity !== `${providerName}_LEAD` || isLeadEnabled));

        return entities;
      }

      // If no supported objects, return empty array (no entities to test)
      return [];
    };

    const payload = {
      customer_id: customerId,
      provider: providerName,
      provider_entities: getProviderEntities(),
      query_start_timestamp,
      query_end_timestamp,
    };

    const batchEndpoint = [
      'INTERCOM',
      'LEMLIST',
      'ADOBE_MARKETO',
      'GOOGLE_DRIVE',
      'GOOGLE_DOCS',
      'GOOGLE_SLIDES',
      'SMARTLEAD',
    ].includes(providerName)
      ? BATCH_EXTRACT_V3
      : ['SALESFORCE', 'PIPEDRIVE', 'FRESHSALES'].includes(providerName)
      ? BATCH_EXTRACT
      : BATCH_EXTRACT_V2;

    const extractRes = await apiClient.post(batchEndpoint, payload, { headers });
    const filteredEntities = getTransformEntities(
      providerName,
      extractRes.data.provider_entity_s3_urls,
    );
    if (filteredEntities.length === 0) return data;

    // use the s3 urls as payload to fetch test data
    const testRunPayload = {
      provider: providerName,
      provider_entity_s3_urls: filteredEntities,
    };

    const testRunRes = await apiClient.post(POST_TEST_RUN, testRunPayload, { headers });

    if (testRunRes.data) {
      // Handle new transform API response structure
      // Response has campaign_contacts array with sprouts_contact data
      if (testRunRes.data.campaign_contacts && Array.isArray(testRunRes.data.campaign_contacts)) {
        // Extract all sprouts_contact data from campaign_contacts array
        const sproutsContacts = testRunRes.data.campaign_contacts
          .map((item: any) => item.sprouts_contact)
          .filter(Boolean); // Filter out any null/undefined values

        if (sproutsContacts.length > 0) {
          // Create a key based on provider_entities to match existing structure
          const entityKey = getProviderEntities()[0]; // Use first entity as key
          data[entityKey] = sproutsContacts;
        }
      } else {
        // Fallback to old response structure if not new format
        data = { ...testRunRes.data };
        Object.values(data).forEach((entityData: any) => {
          entityData.forEach((val: any) => {
            if (val['technologies'] && Array.isArray(val.technologies)) {
              val['technologies'] = val['technologies'].join(', ');
            }
          });
        });
      }
    }
    return data;
  },
);

/**
 * Async thunk to fetch mapping values table data.
 * This thunk fetches mapping values data for a specific field.
 */
export const getMapValuestableData = createAsyncThunk<
  ITableData,
  {
    numericCustomerId: number;
    emailId: string;
    sproutsField: string;
    integrationField: string;
    customerId: string;
    providerName: string;
    entityName: string;
    customeFieldValues?: (string | number)[];
  }
>(
  'integration-v2/getMapValuesTableData',
  async ({
    numericCustomerId,
    emailId,
    sproutsField,
    customerId,
    entityName,
    integrationField,
    providerName,
    customeFieldValues,
  }) => {
    // Make all API requests concurrently
    const [mappedValuesResponse, enumValuesResponse, crmDropdownValuesResponse] = await Promise.all(
      [
        apiClient.get(`${MAP_VALUES_FETCH}?customer_id=${numericCustomerId}`),
        apiClient.get(`${FETCH_ENUM_VALUES}?columnName=${sproutsField}&email=${emailId}`),
        apiClient.post(CRM_DROPDOWN_VALUES, {
          email: emailId,
          customerId,
          fieldName: integrationField,
          providerEntity: entityName,
          provider: providerName,
        }),
      ],
    );
    const mappedValues: IMapValuesRes[] = mappedValuesResponse.data;
    const enumValues = enumValuesResponse.data;
    const crmDropdownValues = crmDropdownValuesResponse.data;

    const currentFieldMappedValues = mappedValues.find(
      (val) => val.fieldName === sproutsField && val.crmKey === integrationField,
    );

    const dropdownData = Object.entries(crmDropdownValues).map(([label, value]) => ({
      label,
      value: (integrationField.startsWith('cf_') ? label : value) as string,
    }));

    const mappingJsonFromDb = JSON.parse(currentFieldMappedValues?.mappingJson || '{}');
    const rowData = (customeFieldValues?.length ? customeFieldValues : enumValues || []).map(
      (value: string) => {
        const val = value || '';
        const integrationValue =
          mappingJsonFromDb[val] ||
          (!currentFieldMappedValues && // auto map if no mapping is present
            dropdownData.find(
              (d) =>
                val.toLowerCase() === d.label.toLowerCase() ||
                val.toLowerCase() === d.value.toLowerCase(),
            )?.value) ||
          '';
        return {
          sproutsValue: val,
          id: val,
          integrationValue,
          status: false,
        };
      },
    );

    const tableData = { rowData, commonData: { integrationValue: dropdownData } } as ITableData;
    return tableData;
  },
);

/**
 * Fetch dropdown values for picklist fields in conditional sync
 * This is a simplified version optimized for conditional sync UI
 */
export const getPicklistDropdownValues = createAsyncThunk<
  { label: string; value: string }[],
  {
    emailId: string;
    customerId: string;
    fieldName: string;
    providerEntity: string;
    provider: string;
  }
>(
  'integration-v2/getPicklistDropdownValues',
  async ({ emailId, customerId, fieldName, providerEntity, provider }) => {
    const response = await apiClient.post(CRM_DROPDOWN_VALUES, {
      email: emailId,
      customerId,
      fieldName,
      providerEntity,
      provider,
    });

    const crmDropdownValues = response.data;

    // Transform response to dropdown options
    const dropdownOptions = Object.entries(crmDropdownValues).map(([label, value]) => ({
      label,
      value: (fieldName.startsWith('cf_') ? label : value) as string,
    }));

    return dropdownOptions;
  },
);

/**
 * This thunk maps field values from Sprouts to Integrations.
 */
export const mapFieldValues = createAsyncThunk<
  void,
  {
    numericCustomerId?: number;
    emailId: string;
    fieldName: string;
    crmKey: string;
    fieldType: string;
    tableData: ITableData;
  }
>(
  'integration-v2/mapFieldValues',
  ({ numericCustomerId, crmKey, emailId, fieldName, fieldType, tableData }) => {
    const mappingJson: Record<string, string> = {};
    tableData.rowData.forEach((row) => {
      if (row.integrationValue) {
        mappingJson[row.sproutsValue] = row.integrationValue;
      }
    });

    apiClient
      .post(MAP_VALUES, {
        customerId: numericCustomerId,
        emailId,
        fieldName,
        crmKey,
        fieldType,
        mappingJson: JSON.stringify(Object.keys(mappingJson).length > 0 ? mappingJson : {}),
      })
      .then((res) => {
        return res.data;
      });
  },
);

/**
 * Async thunk to trigger manual sync for specific contacts/accounts.
 * @param ManualSyncContactsPayload - Contains customerId, pkList, entityName, and isManualSyncAsLead flag
 * @returns Success message on completion
 */
export const syncManualTriggerContacts = createAsyncThunk<string, ManualSyncContactsPayload>(
  'integrations-v2/syncManualTriggerContacts',
  async ({ customerId, pkList, entityName, isManualSyncAsLead, emailId }) => {
    const response = await apiClient.post(
      SYNC_MANUAL_TRIGGER_CONTACTS,
      {
        customerId,
        pkList,
        entityName,
        isManualSyncAsLead,
      },
      {
        headers: emailId
          ? {
              'X-Login-Email': emailId,
            }
          : undefined,
      },
    );
    return response.data;
  },
);

/**
 * This thunk updates the global synchronization flag.
 */
export const updateGlobalSyncFlag = createAsyncThunk<
  UpdateGlobalSyncFlagPayload,
  UpdateGlobalSyncFlagPayload
>('integration-v2/updateGlobleSyncFlag', async (payload) => {
  // Filter out invalid conditions using shared utility function
  const cleanEntityConfig = (config: Record<string, any> | undefined) => {
    if (!config) return config;

    const cleaned: Record<string, any> = {};
    Object.entries(config).forEach(([key, value]) => {
      if (value.conditions && Array.isArray(value.conditions)) {
        // Filter valid conditions and format: first condition has no logicalOperator, rest do
        const validConditions = value.conditions
          .filter(isValidSyncCondition)
          .map((condition: any, index: number) => ({
            field: condition.field,
            fieldType: condition.fieldType,
            operator: condition.operator,
            values: condition.values,
            // Only include logicalOperator for conditions after the first (index > 0)
            ...(index > 0 && { logicalOperator: condition.logicalOperator }),
          }));

        cleaned[key] = {
          ...value,
          conditions: validConditions,
        };
      } else {
        // No conditions array, normalize to empty array so backend can persist the config
        cleaned[key] = {
          ...value,
          conditions: [],
        };
      }
    });
    return cleaned;
  };

  const cleanedPayload = {
    ...payload,
    providerEntityInboundConfig: cleanEntityConfig(payload.providerEntityInboundConfig),
    providerEntityOutboundConfig: cleanEntityConfig(payload.providerEntityOutboundConfig),
  };

  // Filter out LEAD entity from outbound config (only Contact and Account supported for outbound conditional sync)
  const filteredOutboundConfig = cleanedPayload.providerEntityOutboundConfig
    ? Object.fromEntries(
        Object.entries(cleanedPayload.providerEntityOutboundConfig).filter(
          ([entityKey]) => !entityKey.includes('_LEAD'),
        ),
      )
    : cleanedPayload.providerEntityOutboundConfig;

  const syncFrequencyValue = cleanedPayload.syncFrequency.value;
  const payloadToSend = {
    ...cleanedPayload,
    providerEntityOutboundConfig: filteredOutboundConfig,
    syncFrequency: syncFrequencyValue,
  };
  await apiClient.put(GLOBAL_SYNC_FLAG, payloadToSend);

  return cleanedPayload;
});

/**
 * This thunk get the global synchronization flag.
 */
export const getGlobalSyncFlag = createAsyncThunk<UpdateGlobalSyncFlagPayload, IntegrationsPayload>(
  'integration-v2/getGlobalSyncFlag',
  async ({ emailId, providerName, customerId }) => {
    const res = await apiClient.get(
      `${GLOBAL_SYNC_FLAG}?customer=${customerId}&provider=${providerName}&emailId=${emailId}&condition=1`,
      { params: {} },
    );
    const data = res.data;
    return {
      ...data,
      providerName,
      syncFrequency:
        PollingFrequencyOptions.find((option) => option.value === data.syncFrequency) ||
        PollingFrequencyOptions[0],
    };
  },
);

/**
 * Async thunk to disconnect a schedule.
 * This thunk disconnects a schedule associated with an integration.
 */
export const disconnectSchedule = createAsyncThunk<void, IntegrationsPayload>(
  'integration-v2/disconnectSchedule',
  async (payload) => {
    await apiClient.post(DISCONNECT_SCHEDULE, payload);
  },
);

/**
 * Test AUTH_ONLY integration connection
 * Verifies OAuth token works and fetches basic metadata
 *
 * @param customerId - Customer unique ID
 * @param providerName - Integration provider (GMAIL, SLACK, etc.)
 * @param emailId - User email
 * @param useMock - Force use of mock data (for testing)
 * @returns ConnectionTestResult with metadata and status
 */
export const testAuthOnlyConnection = createAsyncThunk<
  any,
  { customerId: string; providerName: string; emailId: string; useMock?: boolean }
>(
  'integration-v2/testAuthOnlyConnection',
  async ({ customerId, providerName, emailId, useMock = true }, { getState }) => {
    const state: any = getState();
    const featureFlags = state?.featureFlags || {};
    const isMockMode = featureFlags?.['mockIntegration'] === true || useMock;

    // Use mock data if mock mode is enabled or backend API not ready
    if (isMockMode) {
      // Import mock generator dynamically
      const { simulateMockConnectionTest } = await import(
        'src/features/settings/integrations/utils/mockConnectionTest'
      );

      const mockResult = await simulateMockConnectionTest(providerName, customerId, emailId);

      return mockResult;
    }

    try {
      const response = await apiClient.post(
        '/integrations/test-connection',
        {
          customerId,
          providerName,
          emailId,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );

      return {
        success: true,
        provider: providerName,
        metadata: response.data.metadata || {},
        summary: response.data.summary || `${providerName} connected successfully`,
        testedAt: new Date().toISOString(),
      };
    } catch (error: any) {
      console.error(`[Test Connection] API call failed for ${providerName}:`, error);

      const { simulateMockConnectionTest } = await import(
        'src/features/settings/integrations/utils/mockConnectionTest'
      );

      const mockResult = await simulateMockConnectionTest(providerName, customerId, emailId);

      return {
        ...mockResult,
        error: 'Used mock data - API endpoint not available',
      };
    }
  },
);

const CRM_TOOL_IDS = new Set([
  'salesforce',
  'hubspot',
  'zoho',
  'microsoft',
  'pipedrive',
  'freshsales',
]);
const CMP_TOOL_IDS = new Set([
  'outreach',
  'salesloft',
  'lemlist',
  'heyreach',
  'instantly',
  'mailchimp',
  'pardot',
]);

export const fetchActiveIntegrations = createAsyncThunk<
  { hasConnectedCrm: boolean; hasConnectedCmp: boolean },
  { customerId: string; emailId: string }
>(
  'integration-v2/fetchActiveIntegrations',
  async ({ customerId, emailId }, { dispatch, getState }) => {
    // Step 1: get the list of tool keys the customer has started
    const { data: numericCustomerId } = await apiClient.get(
      `${GET_CUSTOMER_ID}?uniqueId=${customerId}`,
    );
    const res = await apiClient.post(
      `${FETCH_ACTIVE_INTEGRATIONS}/${numericCustomerId}?email=${encodeURIComponent(emailId)}`,
      {},
    );
    const responseData = res?.data;

    const activeIntegrationKeys: string[] = [
      ...(Array.isArray(responseData?.CRM) ? responseData.CRM : []),
      ...(Array.isArray(responseData?.CMP) ? responseData.CMP : []),
    ]
      .map((item: any) => (typeof item === 'string' ? item : item?.key))
      .filter(Boolean)
      .map((k: string) => String(k).toLowerCase());

    // Step 2: for each active tool, fetch its actual integration status
    const activeTools = activeIntegrationKeys
      .map((toolKey) => IntegrationsTools[toolKey as keyof typeof IntegrationsTools])
      .filter(Boolean);

    await Promise.allSettled(
      activeTools.map(({ providerName, type }) =>
        (dispatch as any)(
          getIntegrationStatus({ customerId, emailId, providerName, integrationType: type }),
        ).unwrap(),
      ),
    );

    // Step 3: read updated Redux state — a tool is connected only if its status is not in DisconnectedStatus
    const state = getState() as any;
    const tools = state.integrationsV2.integrationTools;

    const hasConnectedCrm = activeIntegrationKeys.some(
      (key) => CRM_TOOL_IDS.has(key) && !DisconnectedStatus.includes(tools[key]?.integrationStatus),
    );
    const hasConnectedCmp = activeIntegrationKeys.some(
      (key) => CMP_TOOL_IDS.has(key) && !DisconnectedStatus.includes(tools[key]?.integrationStatus),
    );

    return { hasConnectedCrm, hasConnectedCmp };
  },
);
